export const meta = {
  id: '20-phrases-2-and-colors',
  order: 20,
  titleFi: 'Fraasit 2 ja värit',
  titleEn: 'Phrases 2 and colours',
  duolingoUnit: ['duo-22-phrases-2', 'duo-23-colors'],
  lemmaIds: [
    'ēngoso',
    'ēngos',
    'udrimmi',
    'zaldrīzerme',
    'averilloma',
    'timpa',
    'zōbrie',
    'qeldlie',
    'mele',
    'rhaegal',
    'sētegon',
    'sȳndrilla',
    'ours:zōbrilla', 'ours:qlādīllītsos', 'ours:mādītsos',
    'ours:lettella',
    'ours:vagābvos',
  ],
  exampleIds: [
    'got-s3e04-008',
    'got-s3e06-008',
    'ex-6d56e1237552',
    'ex-e0b4097c544a',
    'ex-c1203ce0631a',
    'ex-ff8f69a8b78a',
    'ex-69b904940d3b',
    'got-s5e03-003',
    'ex-02849290c0b4',
    'ex-6c81f0e08b6f',
    'ex-ab3c7b27b112',
    'ex-b851d00e1fba',
    'ex-f6095ea6445a',
    'ex-7efa15668b36',
    'ex-0e4f7aa62905',
    'hotd-s1e07-011',
    'ex-8aaca64fdb56',
    'ex-bf75a22b4e19',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Phrases_2',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Instrumental',
    'https://wiki.languageinvention.com/index.php?title=Ēngos',
    'https://wiki.languageinvention.com/index.php?title=Ēngoso',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Colors',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar#Borrowing_or_Foreign_Type',
    'https://wiki.languageinvention.com/index.php?title=Rhaegal',
    'https://wiki.languageinvention.com/index.php?title=Sȳndrilla',
    'https://archiveofourown.org/works/41199249/chapters/105725109',
    'https://dedalvs.tumblr.com/post/181114607984/hi-david-this-hv-duolingo-course-is',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['declensions', 'adj-agreement', 'word-building'],
  showLines: ['got-s3e04-008', 'got-s3e06-008', 'got-s5e03-003', 'hotd-s1e07-011'],
  pronunciation: { focus: 'r', words: ['udrimmi', 'zaldrīzerme', 'averilloma', 'zōbrie', 'Rhaegal'], lines: ['got-s3e04-008', 'got-s3e06-008', 'got-s5e03-003'] },
  letter: {
    fi: 'Kuvaile väreillä jotakin, mitä näit tänään.',
    en: 'Describe something you saw today with colours.',
  },
  scene: 'got-s5e03-1',
}

# Fraasit 2 ja värit

Kaksi kevyttä Duolingon osiota samalla viikolla: substantiivit, jotka toimivat kuin adjektiivit, ja korkeavalyrian viisi päävärisanaa. Kumpikin kertoo, miten kieli rakentaa kuvailevia sanoja.

## Fraasit 2

Tämä osa on lyhyt ja kevyt: yksi ilmiö ja viisi sanaa. Ilmiö on kuitenkin hauska, ja se kertoo jotakin olennaista siitä, miten korkeavalyria rakentaa merkityksiä. Joskus tietyssä sijassa ja luvussa oleva substantiivi alkaa toimia kuin adjektiivi: ’kielellä’ tarkoittaa ’puheliasta’, ’viinin kanssa’ ’humalaista’.

## Kieli, jolla puhutaan

Aloitetaan sanasta <Hv glossed>ēngos</Hv>. Se tarkoittaa ’kieli’ – sekä suussa olevaa elintä että kieltä, jota puhutaan. Puhutusta kielestä käytettynä se on sanakirjan mukaan lähempänä murretta; kieli sanan varsinaisessa merkityksessä on <Hv l="udrir">udrir</Hv>. Daenerys käyttää sanaa <Hv glossed>ēngos</Hv> puhuessaan äidinkielestään:

<Line id="got-s3e04-008" />

<Pronounce word="ēngos" />

## Ēngoso: substantiivi adjektiivina

Sanan <Hv>ēngos</Hv> yksikön instrumentaali on <Seam l="ēngos" s="ēng" e="oso" />. <Term id="instrumentaali">Instrumentaaliin</Term> palaamme myöhemmin, mutta lyhyesti: se ilmaisee välinettä, ’jollakin, jonkin avulla’. Thoros käyttää juuri tätä muotoa kysyessään yleiskielestä – kirjaimellisesti ”puhua yleisellä kielellä”:

<Line id="got-s3e06-008" />

<Note>
Thorosin <Hv>ȳdrassis</Hv> on Petersonin mukaan virhe: valyriassa ei puhutella kohteliaasti kolmannessa persoonassa, joten oikein olisi 2. persoona <Hv>ȳdrassia</Hv> (Petersonin Tumblr-vastaus 2018; myös wikin alaviite).
</Note>

Kun <Hv glossed>ēngoso</Hv> on **substantiivin edellä**, se saa adjektiivin merkityksen ’puhelias, suulas’ – sellainen, jolla kieli on ahkerassa käytössä. Duolingon Tips & Notesin esimerkki:

<Interlinear hv="Ēng|oso hont|es vāed|as." gloss="kieli-INS lintu-NOM laulaa-PRS.3SG" fi="Puhelias lintu laulaa." />

Sana sanalta -käännös näyttää sanan todellisen rakenteen: muodoltaan se on yhä sanan ’kieli’ instrumentaali, mutta merkitykseltään se kuvaa lintua.

<Example id="ex-6d56e1237552" />

## Neljä adjektiivista substantiivia

Tällä oppitunnilla opit neljä tällaista sanaa:

- <Hv glossed>udrimmi</Hv> ’nokkela, sanavalmis’
- <Hv glossed>zaldrīzerme</Hv> ’tuhoutumaton, voittamaton’
- <Hv glossed>averilloma</Hv> ’humalainen, hiprakassa’
- <Hv glossed>ēngoso</Hv> ’puhelias, suulas’

Duolingon ohjeet selittävät rakenteen vain sanasta <Hv l="ēngoso">ēngoso</Hv>. Kolme muutakin ovat substantiivien taivutusmuotoja, ja sovellus tunnistaa ne: <Hv glossed>averilloma</Hv> on muodoltaan sanan <Hv glossed>averilla</Hv> ’viini’ yksikön <Term id="komitatiivi">komitatiivi</Term>, <Hv glossed>zaldrīzerme</Hv> sanan <Hv glossed>zaldrīzes</Hv> ’lohikäärme’ <Term id="kollektiivi">kollektiivin</Term> komitatiivi ja <Hv glossed>udrimmi</Hv> sanan <Hv glossed>udir</Hv> ’sana’ <Term id="muutama">muutama</Term>-luvun komitatiivi. Komitatiivi on ’jonkun kanssa’ -sija, joka tulee vastaan myöhemmin. Muistisääntö: viinin kanssa, kaikkien lohikäärmeiden kanssa, muutaman sanan kanssa.

<Pronounce l="udrimmi" word="udrimmi" />

<Pronounce l="zaldrīzerme" word="zaldrīzerme" />

<Pronounce l="averilloma" word="averilloma" />

## Kolme sääntöä

**1. Etuasema.** Näitä sanoja käytetään lähes aina substantiivin edellä, ja Duolingon ohjeet neuvovat pitämään ne toistaiseksi siellä. Muualla niitä näkee harvoin, samoin predikatiivina (*poika on nokkela*).

<Example id="ex-e0b4097c544a" />

**2. Ei mukautumista.** Vaikka merkitys on adjektiivin, sana on yhä substantiivi. Siksi se **ei mukaudu** kuvaamaansa sanaan suvussa, luvussa eikä sijassa. Tavallinen adjektiivi muuttuisi – kuusukuisen sanan kanssa <Hv>ēngenka</Hv>, vesisukuisen kanssa <Hv>ēngenkor</Hv> – mutta <Hv l="averilloma">averilloma</Hv> pysyy samana:

<Example id="ex-c1203ce0631a" />

Tässä <Hv glossed>voktys</Hv> ’pappi’ on aurinkosukua. Adjektiivinen substantiivi pysyy samana silloinkin, kun kuvattava sana on akkusatiivissa – <Hv glossed>jenti</Hv> on sanan <Hv glossed>jentys</Hv> ’johtaja’ akkusatiivi, mutta <Hv l="zaldrīzerme">zaldrīzerme</Hv> ei muutu:

<Example id="ex-ff8f69a8b78a" />

Suomessakin on tällainen taipumaton määrite: **genetiiviattribuutti**. Adjektiivi mukautuu (*sininen lippu*, *siniselle lipulle*), mutta genetiivi ei (*Suomen lippu*, *Suomen lipulle*). Valyrian adjektiivinen substantiivi käyttäytyy kuin suomen genetiiviattribuutti: se on valmiiksi taivutettu muoto ja pysyy sellaisenaan.

<Compare hvFi="voittamattoman johtajan" fi="Suome|n lipun" hv="zaldrīzerme jent|i" label="taipumaton määrite: genetiiviattribuutti ~ adjektiivinen substantiivi (akkusatiivissa)" />

**3. Merkitys kuuluu sanastoon.** Kaikki substantiivit eivät toimi näin. Duolingon ohjeiden mukaan tällaisia sanoja tulee vastaan silloin tällöin, ja ne opetellaan sanoina eikä sääntönä.

<Note>
Predikatiivinen käyttö on harvinaista mutta mahdollista. Tässä esimerkissä kumpikin sana on predikatiivi, ja jälkimmäinen lause on kielteinen, joten verbi on subjunktiivissa:

<Example id="ex-69b904940d3b" />
</Note>

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita. Jokaisessa adjektiivinen substantiivi on etuasemassa ja samassa muodossa riippumatta siitä, mitä se kuvaa.

<Practice hv="Udrimmi riña ȳdras." fi="Nokkela tyttö puhuu." />

<Practice hv="Averilloma voktys averille mōzus." fi="Humalainen pappi juo viiniä." />

<Practice hv="Ēngoso taobot rūklon tepan." fi="Annan puheliaalle pojalle kukan." />

<Practice hv="Zaldrīzerme azantys hontī rȳbos daor." fi="Voittamaton ritari ei kuule lintuja." />

Kolmannessa lauseessa <Hv fi="pojalle">taobot</Hv> on datiivissa, mutta <Hv l="ēngoso">ēngoso</Hv> ei muutu – aivan kuten *Suomen* ei muutu ilmauksessa *Suomen lipulle*.

## Kuuluisat repliikit

Opettele nämä neljä tuttua repliikkiä nyt kokonaisina, vaikka kaikkea niiden kielioppia ei ole vielä käyty. Kuuntele, sano perässä ja äänitä itsesi. Puramme ne osiin myöhemmin: korppien sananlaskun värisanat jo tällä oppitunnilla ja sen siivet <LessonLink n={35}>oppitunnilla 35</LessonLink>, Lannisterien tunnuslauseen <LessonLink n={38}>oppitunnilla 38</LessonLink>, taisteluoikeudenkäyntiä pyytävän lauseen oppitunneilla <LessonLink n={26}>26</LessonLink> ja <LessonLink n={39}>39</LessonLink> sekä Yövartion hautajaissanat <LessonLink n={40}>oppitunnilla 40</LessonLink>. Ajatus opetella repliikit ensin kokonaisina on Damien Halmen (Academy of High Valyrian).

<Repeat>
<Example id="ex-7efa15668b36" />
<Example id="ex-d01928b2ea35" />
<Example id="ex-26431af6f541" />
<Example id="ex-5392f63a5a8c" />
</Repeat>

## Värit

Duolingon Tips & Notesin mukaan englannissa on yksitoista perusvärisanaa, ja suomessa suunnilleen samat: valkoinen, musta, punainen, vihreä, keltainen, sininen, ruskea, harmaa, oranssi, violetti ja vaaleanpunainen. Korkeavalyria jakaa värikirjon toisin. Siinä on vain viisi päävärisanaa, ja ne kattavat laajoja alueita, usein niin, että samaan sanaan kuuluu sävyjä, joita suomalainen pitää eri väreinä. Tällä oppitunnilla opit nämä viisi sanaa ja sen, miten kieli taivuttaa lainasanoja. Esimerkkinä on Daenerysin lohikäärme Rhaegal.

Aloitetaan lauseella, jonka väkijoukko huutaa yhteen ääneen punaisen papittaren saarnassa:

<Line id="got-s5e03-003" />

<Hv glossed>Zōbrie</Hv> on tässä ’pimeä’ – värisana, joka kattaa myös mustan.

## Viisi väriä

Duolingon ohjeet kuvaavat järjestelmän näin:

- <Hv glossed>timpa</Hv>: vaaleat sävyt sinisestä vaaleanpunaiseen
- <Hv glossed>zōbrie</Hv>: tummat sävyt sinisestä violettiin
- <Hv glossed>qeldlie</Hv>: vaaleat sävyt punaisesta keltaiseen
- <Hv l="mele" glossed>mele</Hv>: tummat sävyt oranssista punaiseen
- <Hv glossed>kasta</Hv>: sekä tummat että vaaleat sävyt sinisestä vihreään

<Hv fi="sininen, vihreä">Kasta</Hv> on sinulle jo tuttu datiivin oppitunnilta, jossa se oli mallisanana. Jako ei ole aukoton, mutta Duolingon ohjeiden mukaan puhujat ovat yleensä hyvin yksimielisiä siitä, millä sanalla jonkin todellisen esineen väri ilmaistaan.

Näin suomen perusvärit sijoittuvat valyriaan:

- **valkoinen**: <Hv glossed>timpa</Hv>
- **musta**: <Hv glossed>zōbrie</Hv>
- **punainen**: <Hv l="mele" glossed>mele</Hv>
- **vihreä**: <Hv glossed>kasta</Hv>
- **sininen**: <Hv glossed>kasta</Hv>
- **keltainen**: <Hv glossed>qeldlie</Hv>
- **ruskea**: vaalea <Hv glossed>qeldlie</Hv>, tumma <Hv l="mele" glossed>mele</Hv>
- **harmaa**: vaalea <Hv glossed>timpa</Hv>, tumma <Hv glossed>zōbrie</Hv>
- **oranssi**: vaalea <Hv glossed>qeldlie</Hv>, tumma <Hv l="mele" glossed>mele</Hv>
- **violetti**: <Hv glossed>zōbrie</Hv>
- **vaaleanpunainen**: <Hv glossed>timpa</Hv>

Ja toisin päin, valyriasta suomeen:

- <Hv glossed>timpa</Hv>: valkoinen, vaaleanpunainen, vaaleanharmaa
- <Hv glossed>zōbrie</Hv>: musta, violetti, tummanharmaa
- <Hv glossed>qeldlie</Hv>: keltainen, vaalea oranssi, vaaleanruskea
- <Hv l="mele" glossed>mele</Hv>: punainen, tumma oranssi, tummanruskea
- <Hv glossed>kasta</Hv>: sininen, vihreä

Suomesta ei tässä ole apua, mutta ei haittaakaan: kyse on vain siitä, mihin kohtaan värikirjoa raja vedetään. Valyrian värisanoja on helpointa ajatella **vaalea–tumma**-akselilla. Kysy ensin, onko väri vaalea vai tumma, ja vasta sitten, millä alueella se on.

Rautavaltaistuin on harmaa, ja sanavalinnasta näkee, että harmaa on tässä tumma: <Hv glossed>zōbrie</Hv>, maasuvun muodossa.

<Example id="ex-02849290c0b4" />

<Pronounce word="zōbrie" />

<Pronounce word="qeldlie" />

## Värit ovat adjektiiveja

Värisanat ovat tavallisia adjektiiveja ja mukautuvat kuten muutkin. Ne kuuluvat eri luokkiin: <Hv>timpa</Hv> ja <Hv>kasta</Hv> ovat <Term id="adjektiiviluokka">I-luokkaa</Term>, <Hv l="mele">mele</Hv> <Term id="adjektiiviluokka">II-luokkaa</Term>, <Hv>zōbrie</Hv> ja <Hv>qeldlie</Hv> <Term id="adjektiiviluokka">III-luokkaa</Term>. Katso, miten <Hv l="mele">mele</Hv> muuttuu etuasemassa suvun mukaan:

<Paradigm lemma="mele" filter="positive.pre" cases="nom,acc" numbers="sg,pl" />

Esimerkeistä näkyy, miten saman värin muoto riippuu siitä, mitä se kuvaa. Lintu on aurinkosukua, joten valkoinen on <Hv glossed>timpys</Hv>. Lohikäärmekin on aurinkosukua, ja kultainen on <Hv glossed>qeldlie</Hv>, koska <Term id="adjektiiviluokka">III-luokassa</Term> kuu- ja aurinkosuvun muodot ovat samat:

<Example id="ex-6c81f0e08b6f" />

<Example id="ex-ab3c7b27b112" />

Kilpikonna on vesisukua, joten vihreä on <Hv glossed>kastor</Hv>:

<Interlinear hv="Kast|or qint|ir aver|a iprad|as." gloss="vihreä-AQ.NOM kilpikonna-NOM viinirypäle-ACC.PL syödä-PRS.3SG" fi="Vihreä kilpikonna syö viinirypäleitä." />

<Example id="ex-b851d00e1fba" />

Laivat ovat vesisukua ja monikossa, joten musta on <Hv glossed>zōbriar</Hv>:

<Example id="ex-f6095ea6445a" />

Sanonnassa *Dark wings, dark words* (”mustat siivet, mustat sanat”) sama värisana on kahdessa eri muodossa, koska siivet ovat aurinkosukua ja sanat vesisukua:

<Example id="ex-7efa15668b36" />

Värit voivat kuvata myös ihmisryhmiä. Punaiset papit ovat <Hv glossed>Meli Voktyssy</Hv>, ja *vihreät* – Rhaenyran vastapuoli – ovat <Hv l="kasta" glossed>Kastoti</Hv>, joka on sanan <Hv fi="sininen, vihreä">kasta</Hv> muoto:

<Example id="ex-0e4f7aa62905" />

<Line id="hotd-s1e07-011" />

<Note>
Peterson on itse sanonut repliikin *mērpī*-sanan virheeksi: se on adjektiivin muoto, ja adverbina oikea muoto olisi <Hv l="mērprī">mērprī</Hv> tai <Hv l="mērprī">mērpirī</Hv> (Petersonin Conlang Dialogue -selitykset, 2022).
</Note>

## Sȳndrilla – väri on aine

Sana ’väri’ on <Hv>sȳndrilla</Hv>. Tunnistatko lopun? Se on ruoan oppitunnilta tuttu <Hv>-illa</Hv>-johdin: sana näyttää kuusukuiselta mutta on vesisukua. Siksi kysymyssana <Hv glossed>skorior</Hv> ’mikä’ on alla olevassa lauseessa vesisuvun muodossa – sama ilmiö kuin <Hv fi="maukas viini">ēngenkor averilla</Hv>:

<Example id="ex-8aaca64fdb56" />

<Pronounce word="sȳndrilla" />

Tämän oppitunnin verbi <Hv glossed>sētegon</Hv> tarkoittaa ’tehdä, luoda’ mutta myös ’takoa’ ja ’muovata ainetta’. Häämenojen säkeissä sydämet on taottu tulessa:

<Example id="ex-bf75a22b4e19" />

## Lainasanat: Rhaegal

Jokainen kieli lainaa sanoja. Monet lainasanat sopivat johonkin valmiiseen <Term id="deklinaatio">taivutusluokkaan</Term> (esimerkiksi <Hv glossed>septon</Hv> ’septoni’ kuuluu kolmanteen), mutta kaikki eivät. Yksi niistä on Daenerysin lohikäärme **Rhaegal** (Duolingon ohjeiden esimerkki): korkeavalyriassa ei enää ole <Hv>l</Hv>-loppuisia substantiiveja, joten tällaiset nimet kuuluvat **lainasanojen yleiseen <Term id="deklinaatio">taivutusluokkaan</Term>**, joka on aina kuusukua.

Taivutus poikkeaa aiemmin opituista. Taulukossa ovat tähän mennessä opitut sijat:

<Paradigm lemma="rhaegal" cases="nom,acc,gen,dat,loc" numbers="sg,pl" />

Huomaa muutama asia. Yksikön akkusatiivi <Hv glossed>Rhaegali</Hv> on sama kuin monikon nominatiivi. Monikon genetiivi, datiivi ja lokatiivi ovat kaikki <Hv glossed>Rhaegaloti</Hv>. Yksikön lokatiivi <Hv glossed>Rhaegalī</Hv> taas on sama kuin monikon akkusatiivi. Wikin kieliopin mukaan samaan kuudenteen taivutukseen kuuluvat muutkin lainasanat, kuten <Hv glossed>buzdari</Hv> ’orja’, jonka tunnet kieltolauseesta <Hv>Zaldrīzes buzdari iksos daor</Hv>.

Suomessa lainasanoille käy samoin: vierasperäinen nimi sovitetaan johonkin olemassa olevaan taivutustyyppiin. Konsonanttiin päättyvä nimi saa suomessa sidevokaalin *i*: *Rhaegal* → *Rhaegalin*, *Rhaegalille*. Valyrian datiivi muodostetaan tuttuun tapaan genetiivistä: <Hv>Rhaegalo</Hv> → <Hv>Rhaegalot</Hv>.

<Compare fi="Rhaegal|ille" hv="Rhaegal|ot" label="allatiivi ~ datiivi" />

<Pronounce word="Rhaegal" />

Kirjainyhdistelmä *rh* on soinniton r: se on kuin suomen r, mutta ilman äänihuulten värähtelyä, ikään kuin kuiskaten. Paino on ensimmäisellä tavulla.

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita.

<Practice hv="Zaldrīzes zōbrie issa." fi="Lohikäärme on musta." />

<Practice hv="Riña meli prūbrī ipradas." fi="Tyttö syö punaiset omenat." />

<Practice hv="Timpon havon ipradan." fi="Syön vaaleaa leipää." />

<Practice hv="Rhaegalot parklon tepan." fi="Annan Rhaegalille lihaa." />

## Sanat

<Vocab />

<Check />
